import { THEMES, selectedTheme, setTheme, applyChrome } from './themes';
import { selectedBackend, setBackend, type BackendStatus } from './api';

// SOUND_KEY holds the turn-done chime preference. Stored as '1' (on) / '0' (off);
// an unset value defaults to ON.
const SOUND_KEY = 'zw_sound';

// soundEnabled reports whether the turn-done chime is on. Default ON: an unset
// pref returns true. Pure — pass `stored` explicitly in tests.
export function soundEnabled(stored: string | null = localStorage.getItem(SOUND_KEY)): boolean {
  return stored !== '0';
}

// setSoundEnabled persists the chime preference.
export function setSoundEnabled(on: boolean): void {
  localStorage.setItem(SOUND_KEY, on ? '1' : '0');
}

export interface SettingsCallbacks {
  // onBackendChange fires after the user picks a different backend so the shell
  // can reload its list against the new target.
  onBackendChange: () => void;
}

// openSettings mounts the Settings sheet as an overlay: a Theme picker, a Backend
// picker (only when ≥2 backends exist), and the Sound-notifications toggle.
// Full-screen on phone, a right-hand side panel on wide (CSS-driven). Theme,
// backend, and sound apply + persist immediately; closes on backdrop or Done.
export function openSettings(backends: BackendStatus[], cb: SettingsCallbacks): void {
  const root = document.createElement('div');
  root.className = 'settings-overlay';

  const currentTheme = selectedTheme().id;
  const themeItems = THEMES.map(
    (t) =>
      `<button class="theme-item" role="menuitemradio" data-theme="${t.id}" aria-checked="${
        t.id === currentTheme ? 'true' : 'false'
      }"><span class="theme-swatch" style="background:${t.ui.bg};border-color:${t.ui.accent}"></span>${t.name}</button>`
  ).join('');

  const backendGroup =
    backends.length >= 2
      ? `<div class="setting-group">
          <div class="setting-label">Backend</div>
          <select id="settings-backend">${backends
            .map((b) => {
              const cur = b.id === selectedBackend(backends);
              return `<option value="${b.id}"${cur ? ' selected' : ''}${b.online ? '' : ' disabled'}>${
                b.name
              }${b.online ? '' : ' (offline)'}</option>`;
            })
            .join('')}</select>
        </div>`
      : '';

  const soundOn = soundEnabled();
  root.innerHTML = `
    <div class="settings-scrim"></div>
    <section class="settings-panel" role="dialog" aria-label="Settings" aria-modal="true">
      <header class="settings-head">
        <h2>Settings</h2>
        <button class="settings-done primary">Done</button>
      </header>
      <div class="settings-body">
        <div class="setting-group">
          <div class="setting-label">Theme</div>
          <div class="theme-list" role="menu">${themeItems}</div>
        </div>
        ${backendGroup}
        <div class="setting-group">
          <div class="setting-row">
            <div>
              <div class="setting-label">Sound notifications</div>
              <div class="setting-hint">Chime when Claude finishes a turn</div>
            </div>
            <button class="toggle${soundOn ? ' on' : ''}" role="switch" aria-checked="${soundOn}" aria-label="Sound notifications"><span class="knob"></span></button>
          </div>
        </div>
      </div>
    </section>`;

  document.body.append(root);

  const close = () => {
    document.removeEventListener('keydown', onKey);
    root.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);
  root.querySelector('.settings-scrim')!.addEventListener('click', close);
  root.querySelector('.settings-done')!.addEventListener('click', close);

  // Theme: persist + re-theme chrome (open terminal re-themes via the event in
  // setTheme), then refresh the checked marks within the panel.
  root.querySelectorAll<HTMLButtonElement>('.theme-item').forEach(
    (item) =>
      (item.onclick = () => {
        const id = item.dataset.theme!;
        setTheme(id);
        applyChrome(selectedTheme(id));
        root
          .querySelectorAll<HTMLButtonElement>('.theme-item')
          .forEach((b) => b.setAttribute('aria-checked', String(b.dataset.theme === id)));
      })
  );

  // Backend: persist + ask the shell to reload its list against the new target.
  const picker = root.querySelector('#settings-backend') as HTMLSelectElement | null;
  if (picker) {
    picker.onchange = () => {
      setBackend(picker.value);
      cb.onBackendChange();
    };
  }

  // Sound: persist + reflect the toggle state.
  const toggle = root.querySelector('.toggle') as HTMLButtonElement;
  toggle.onclick = () => {
    const next = !toggle.classList.contains('on');
    setSoundEnabled(next);
    toggle.classList.toggle('on', next);
    toggle.setAttribute('aria-checked', String(next));
  };
}
